// One canonical AR cash receipt with its append-only invoice applications.
// Later applications change unapplied credit, never the cash journal.
import { formatMoney } from "@sylviq/ui-core";
import { Button } from "@sylviq/ui-react-web/components/ui/button";
import { Dialog } from "@sylviq/ui-react-web/components/ui/dialog";
import { Input } from "@sylviq/ui-react-web/components/ui/input";
import { PageHeader } from "@sylviq/ui-react-web/components/ui/page-header";
import { Select } from "@sylviq/ui-react-web/components/ui/select";
import { StatusDot } from "@sylviq/ui-react-web/components/ui/status-dot";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@sylviq/ui-react-web/components/ui/table";
import { Link, useParams } from "@tanstack/react-router";
import { type FormEvent, useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  applyPayment,
  getPayment,
  listInvoices,
  type Invoice,
  type InvoicePayment,
} from "@/lib/api";
import { humanizeLabel, useInvoiceNumbers } from "@/lib/labels";

function toErrorMessage(cause: unknown): string {
  return cause instanceof Error ? cause.message : String(cause);
}

function toMinor(amount: string): number | null {
  const value = Number(amount.trim());
  if (!Number.isFinite(value) || value <= 0) return null;
  const minor = Math.round(value * 100);
  return Number.isSafeInteger(minor) && minor > 0 ? minor : null;
}

function submissionKey(fingerprint: string, previous: { fingerprint: string; key: string } | null) {
  if (previous?.fingerprint === fingerprint) return previous;
  return { fingerprint, key: globalThis.crypto.randomUUID() };
}

export function ReceiptDetailPage() {
  const { paymentId } = useParams({ strict: false }) as { paymentId: string };
  const [receipt, setReceipt] = useState<InvoicePayment | null>(null);
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [invoiceId, setInvoiceId] = useState("");
  const [amount, setAmount] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [confirming, setConfirming] = useState<{
    invoiceId: string;
    amountMinor: number;
    fingerprint: string;
  } | null>(null);
  const applicationKey = useRef<{ fingerprint: string; key: string } | null>(null);
  const invoiceNumbers = useInvoiceNumbers();

  const reload = useCallback(async () => {
    if (!paymentId) return;
    setError(null);
    try {
      const [loadedReceipt, loadedInvoices] = await Promise.all([
        getPayment(paymentId),
        listInvoices(),
      ]);
      setReceipt(loadedReceipt);
      setInvoices(loadedInvoices);
    } catch (cause) {
      setError(toErrorMessage(cause));
    }
  }, [paymentId]);

  useEffect(() => {
    void reload();
  }, [reload]);

  // Prefill the full remaining unapplied credit; the page has no per-invoice
  // remainder, so the total is the only safe starting amount.
  useEffect(() => {
    if (!receipt) return;
    setAmount((receipt.unapplied_amount_cents / 100).toFixed(2));
  }, [receipt, invoiceId]);

  const eligibleInvoices = useMemo(() => {
    if (!receipt) return [];
    return invoices.filter((invoice) => {
      if (invoice.legal_entity_id !== receipt.legal_entity_id || invoice.status === "void") {
        return false;
      }
      return Boolean(
        receipt.crm_contact_id &&
          invoice.crm_contact_id === receipt.crm_contact_id,
      );
    });
  }, [invoices, receipt]);

  const submitApplication = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!receipt) return;
    setError(null);
    setNotice(null);
    const amount_minor = toMinor(amount);
    if (!invoiceId) {
      setError("Choose an invoice for this application.");
      return;
    }
    if (!amount_minor) {
      setError("Application amount must be positive.");
      return;
    }
    if (amount_minor > receipt.unapplied_amount_cents) {
      setError("Application exceeds the receipt’s unapplied balance.");
      return;
    }
    const fingerprint = JSON.stringify({ paymentId, invoiceId, amount_minor });
    setConfirming({ invoiceId, amountMinor: amount_minor, fingerprint });
  };

  const confirmApplication = async () => {
    if (!receipt || !confirming) return;
    const application = confirming;
    setConfirming(null);
    setError(null);
    setNotice(null);
    const submission = submissionKey(application.fingerprint, applicationKey.current);
    applicationKey.current = submission;
    setBusy(true);
    try {
      await applyPayment(paymentId, {
        invoice_id: application.invoiceId,
        amount_minor: application.amountMinor,
        idempotency_key: submission.key,
      });
      applicationKey.current = null;
      setAmount("");
      setInvoiceId("");
      setNotice("Applied to the invoice. The receipt journal was not changed.");
      await reload();
    } catch (cause) {
      // Keep the same key for an unchanged retry; changing the form payload
      // automatically selects a new stable key.
      setError(toErrorMessage(cause));
    } finally {
      setBusy(false);
    }
  };

  const title = receipt ? `Receipt ${receipt.id.slice(0, 8).toUpperCase()}` : "Receipt";
  const allocations = receipt?.allocations ?? [];
  const confirmingInvoiceLabel = confirming
    ? invoiceNumbers.get(confirming.invoiceId) ??
      eligibleInvoices.find((invoice) => invoice.id === confirming.invoiceId)?.customer_name ??
      confirming.invoiceId
    : "";

  return (
    <div className="grid gap-6">
      <nav aria-label="Breadcrumb" className="text-sm">
        <Link
          to="/receipts"
          className="text-[var(--muted-foreground)] hover:text-[var(--foreground)] hover:underline"
        >
          Receipts
        </Link>
        <span aria-hidden="true" className="mx-2 text-[var(--muted-foreground)]">/</span>
        <span aria-current="page" className="font-medium">{title}</span>
      </nav>

      {error ? (
        <div role="alert" className="surface rounded-xl border border-[var(--danger)] p-4 text-sm text-[var(--danger)]">
          {error}
          <div className="mt-2">
            <Button type="button" variant="secondary" size="sm" onClick={() => void reload()}>
              Retry
            </Button>
          </div>
        </div>
      ) : null}
      {notice ? (
        <output className="surface block rounded-xl border border-[var(--success)] p-4 text-sm text-[var(--success)]">
          {notice}
        </output>
      ) : null}

      {receipt === null && !error ? (
        <p className="text-sm text-[var(--muted-foreground)]">Loading receipt…</p>
      ) : receipt ? (
        <>
          <PageHeader
            title={title}
            description={
              <>
                {receipt.crm_contact_id ? "Linked CRM customer" : "Invoice receipt"} · received{" "}
                {new Date(receipt.received_at).toLocaleDateString()} via {humanizeLabel(receipt.method)} ·{" "}
                <StatusDot
                  tone={receipt.status === "reconciled" ? "success" : "idle"}
                  label={receipt.status === "reconciled" ? "Reconciled" : "Unreconciled"}
                />
              </>
            }
          />

          <div className="grid gap-4 sm:grid-cols-3">
            <div className="surface rounded-2xl p-5">
              <p className="text-xs font-medium text-[var(--muted-foreground)]">Cash received</p>
              <p className="mt-1 text-2xl font-semibold tabular-nums">
                {formatMoney({ cents: receipt.amount_cents, currency: "USD" })}
              </p>
            </div>
            <div className="surface rounded-2xl p-5">
              <p className="text-xs font-medium text-[var(--muted-foreground)]">Applied to invoices</p>
              <p className="mt-1 text-2xl font-semibold tabular-nums">
                {formatMoney({ cents: receipt.applied_amount_cents, currency: "USD" })}
              </p>
            </div>
            <div className="surface rounded-2xl border border-[var(--action)]/30 p-5">
              <p className="text-xs font-medium text-[var(--muted-foreground)]">Unapplied AR credit</p>
              <p className="mt-1 text-2xl font-semibold tabular-nums">
                {formatMoney({ cents: receipt.unapplied_amount_cents, currency: "USD" })}
              </p>
            </div>
          </div>

          <section className="grid gap-3" aria-labelledby="receipt-applications-heading">
            <h2 id="receipt-applications-heading" className="text-lg font-semibold">Invoice applications</h2>
            <div className="surface overflow-hidden rounded-2xl">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Invoice</TableHead>
                    <TableHead>Applied on</TableHead>
                    <TableHead className="text-right">Amount</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {allocations.length === 0 ? (
                    <TableRow><TableCell colSpan={3}>No allocations yet; the receipt remains unapplied AR credit.</TableCell></TableRow>
                  ) : allocations.map((application) => (
                    <TableRow key={application.id}>
                      <TableCell>
                        <Link
                          to="/invoices/$invoiceId"
                          params={{ invoiceId: application.invoice_id }}
                          className="text-[var(--action)] underline-offset-4 hover:underline"
                        >
                          {invoiceNumbers.get(application.invoice_id) ?? "View invoice"} →
                        </Link>
                      </TableCell>
                      <TableCell>{application.applied_at ? new Date(application.applied_at).toLocaleDateString() : "—"}</TableCell>
                      <TableCell className="text-right tabular-nums">
                        {formatMoney({ cents: application.amount_cents, currency: "USD" })}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </section>

          {receipt.unapplied_amount_cents > 0 ? (
            <form onSubmit={submitApplication} className="surface grid gap-3 rounded-2xl p-5" aria-label="Apply receipt credit">
              <div>
                <h2 className="font-semibold">Apply unapplied credit</h2>
                <p className="mt-1 text-sm text-[var(--muted-foreground)]">
                  Pick an invoice for the same CRM contact and legal entity. This records an allocation only; no second journal entry is created.
                </p>
              </div>
              <div className="grid gap-3 sm:grid-cols-[2fr_1fr_auto] sm:items-end">
                <label className="grid gap-1 text-xs font-medium" htmlFor="receipt-apply-invoice">
                  Invoice
                  <Select
                    id="receipt-apply-invoice"
                    value={invoiceId}
                    onChange={setInvoiceId}
                    disabled={busy}
                    placeholder="Choose an invoice…"
                    options={eligibleInvoices.map((invoice) => ({
                      value: invoice.id,
                      label: `${invoiceNumbers.get(invoice.id) ?? invoice.customer_name} — ${formatMoney({ cents: invoice.total_minor, currency: "USD" })}`,
                    }))}
                  />
                </label>
                <label className="grid gap-1 text-xs font-medium" htmlFor="receipt-apply-amount">
                  Amount (USD)
                  <Input
                    id="receipt-apply-amount"
                    type="number"
                    min="0.01"
                    step="0.01"
                    inputMode="decimal"
                    value={amount}
                    onChange={(event) => setAmount(event.target.value)}
                    disabled={busy}
                    required
                  />
                </label>
                <Button type="submit" disabled={busy || eligibleInvoices.length === 0}>
                  {busy ? "Applying…" : "Apply credit"}
                </Button>
              </div>
              {eligibleInvoices.length === 0 ? (
                <p className="text-xs text-[var(--muted-foreground)]">
                  No eligible invoice is available for this receipt’s CRM contact and legal entity.
                </p>
              ) : null}
            </form>
          ) : null}

          {receipt.journal_entry_id ? (
            <p className="text-sm text-[var(--muted-foreground)]">
              Receipt journal: <Link to="/journal/$entryId" params={{ entryId: receipt.journal_entry_id }} className="text-[var(--action)] underline-offset-4 hover:underline">View posted entry →</Link>
            </p>
          ) : null}
        </>
      ) : null}

      <Dialog
        open={confirming !== null}
        onOpenChange={(open) => {
          if (!open) setConfirming(null);
        }}
        title={
          confirming
            ? `Apply ${formatMoney({ cents: confirming.amountMinor, currency: "USD" })} to invoice ${confirmingInvoiceLabel}?`
            : "Apply receipt credit?"
        }
        description="This records an allocation of unapplied credit only; the receipt journal and posted cash are not changed."
      >
        <div className="actions-row">
          <Button type="button" variant="secondary" onClick={() => setConfirming(null)}>
            Cancel
          </Button>
          <Button type="button" disabled={busy} onClick={() => void confirmApplication()}>
            {busy ? "Applying…" : "Apply credit"}
          </Button>
        </div>
      </Dialog>
    </div>
  );
}
